import type { LoaderFunction, MetaFunction } from "@remix-run/cloudflare";
import { useContext, useEffect } from "react";
import { cn } from "@heroui/react";
import { Link, useNavigate } from "@remix-run/react";
import { HomeScreenTopBar } from "~/components/HomeScreenTopBar";
import { AppContext } from "~/context/AppContext";
import { SignedIn, SignedOut, useUser } from "@clerk/remix";
import { FaDiscord } from "react-icons/fa";
import posthog from "posthog-js";
import { SkillLevelDashboard } from "~/components/skills/SkillLevelDashboard";
import { getAuth } from "@clerk/remix/ssr.server";
import { createClient } from "@supabase/supabase-js";
import { Database } from "~/types/supabase";
import { PrimaryButton } from "~/components/PrimaryButton";
import { Hero } from "~/components/landing-page/Hero";

export const meta: MetaFunction = () => {
  return [
    { title: "ManUp" },
    { name: "description", content: "Build confidence in dating." },
  ];
};

export const loader: LoaderFunction = async (args) => {
  const { userId } = await getAuth(args);
  if (userId) {
    const supabaseUrl = args.context.cloudflare.env.SUPABASE_URL;
    const supabaseKey = args.context.cloudflare.env.SUPABASE_KEY;

    if (!supabaseUrl || !supabaseKey) {
      console.log("Missing environment variables");
      throw new Error("Missing environment variables");
    }

    const supabase = createClient<Database>(supabaseUrl, supabaseKey);
    const { data } = await supabase
      .from("users")
      .select("*")
      .eq("user_id", userId)
      .single();
    if (!data) {
      await supabase.from("users").upsert({ user_id: userId });
      const { data: skills } = await supabase.from("skills").select("*");
      if (skills && skills.length > 0) {
        await supabase.from("user_skills").upsert(
          skills.map((skill) => ({
            user_id: userId,
            skill_id: skill.id,
          }))
        );
      }
    }
  }

  return null;
};

export default function Index() {
  const { gameData } = useContext(AppContext);
  const { isSignedIn, user } = useUser();

  const navigate = useNavigate();

  useEffect(() => {
    if (user && isSignedIn) {
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
      });
    }
  }, [user, isSignedIn]);

  const practiceSkill = async () => {
    navigate("/train");
  };

  return (
    <>
      <SignedIn>
        <div
          className={cn(
            "flex flex-col items-center justify-center h-dvh px-4 relative w-full"
          )}
        >
          <div className="absolute top-0 left-0 right-0">
            <HomeScreenTopBar />
          </div>

          <SkillLevelDashboard
            skills={
              gameData?.skills.filter((s) => s.name !== "Dominance") ?? []
            }
            practiceSkill={practiceSkill}
          />

          <div className="mt-10">
            <PrimaryButton
              onClick={() => {
                navigate("/scenarios");
              }}
            >
              Start Game
            </PrimaryButton>

            <PrimaryButton
              onClick={async () => {
                navigate("/train");
              }}
              baseClassName="mt-4 w-full"
              variant="secondary"
            >
              Train
            </PrimaryButton>
          </div>
          {/* discord button */}
          <div className="flex flex-row justify-center items-center gap-4 mt-4 opacity-60 mb-10">
            <Link
              to="https://discord.gg/h3usDWEwY8"
              target="_blank"
              rel="noreferrer"
            >
              <FaDiscord className="w-8 h-8" />
            </Link>
          </div>
        </div>
      </SignedIn>
      <SignedOut>
        <Hero />
      </SignedOut>
    </>
  );
}
